import {
  CheckoutCompletedItem,
  CheckoutCompletedOffer,
  CheckoutCompletedQuote,
  CheckoutCompletedRenewal,
  CheckoutItemDetails,
  CheckoutRequest,
  CheckoutResponse,
  CompletedQuoteDetails,
} from '@checkout/util-models';
import {
  BasketItemQuoteDetails,
  BasketItemType,
  PaymentOption,
  QuoteDetails,
  basketItemToQuotes,
} from '@domgen/dgx-fe-basket';

export function unifyCheckoutResponse(
  responses: CheckoutResponse[]
): CheckoutResponse {
  const { basket, weight } = responses[0];
  const completedItems: CheckoutCompletedItem[] = responses.reduce(
    (items: CheckoutCompletedItem[], response: CheckoutResponse) => [
      ...items,
      ...response.completedItems,
    ],
    []
  );

  return {
    basket,
    completedItems,
    weight,
  };
}

export function formatCheckoutCompletedItems(
  request: CheckoutRequest,
  response: CheckoutResponse
): CheckoutCompletedItem[] {
  let completedItems: CheckoutCompletedItem[] =
    removeUnusedCheckoutCompletedItemQuotes(
      request.items,
      response.completedItems
    );

  completedItems = removeUnusedCheckoutCompletedItemPaymentOptions(
    request,
    completedItems
  );

  return completedItems;
}

export function removeUnusedCheckoutCompletedItemPaymentOptions(
  request: CheckoutRequest,
  completedItems: CheckoutCompletedItem[]
): CheckoutCompletedItem[] {
  return completedItems.map(
    (completedItem: CheckoutCompletedItem) =>
      ({
        ...completedItem,
        item: {
          ...completedItem.item,
          data: {
            ...completedItem.item.data,
            quotes: completedItem.item.data.quotes.map(
              (quote: BasketItemQuoteDetails) => ({
                ...quote,
                paymentOptions: quote.paymentOptions.filter(
                  (paymentOption: PaymentOption) =>
                    paymentOption.paymentType === request.payment.paymentType
                ),
              })
            ),
          },
        },
      } as CheckoutCompletedItem)
  );
}

export function removeUnusedCheckoutCompletedItemQuotes(
  items: CheckoutItemDetails[],
  completedItems: CheckoutCompletedItem[]
): CheckoutCompletedItem[] {
  const quoteIds: string[] = items.map(
    (item: CheckoutItemDetails) => item.quoteId
  );

  return completedItems.map(
    (completedItem: CheckoutCompletedItem) =>
      ({
        ...completedItem,
        item: {
          ...completedItem.item,
          data: {
            ...completedItem.item.data,
            quotes: completedItem.item.data.quotes.filter(
              (quote: BasketItemQuoteDetails) =>
                quoteIds.includes(quote.quoteId)
            ),
          },
        },
      } as CheckoutCompletedItem)
  );
}

export function checkoutCompletedItemsOfType(
  completedItems: CheckoutCompletedItem[],
  type: BasketItemType.Quote
): CheckoutCompletedQuote[];

export function checkoutCompletedItemsOfType(
  completedItems: CheckoutCompletedItem[],
  type: BasketItemType.Offer
): CheckoutCompletedOffer[];

export function checkoutCompletedItemsOfType(
  completedItems: CheckoutCompletedItem[],
  type: BasketItemType.Renewal
): CheckoutCompletedRenewal[];

export function checkoutCompletedItemsOfType(
  completedItems: CheckoutCompletedItem[],
  type: BasketItemType
): CheckoutCompletedItem[] {
  return completedItems.filter(
    (completedItem: CheckoutCompletedItem) =>
      completedItem.item.itemType === type
  );
}

export function checkoutCompletedQuoteToCompletedQuoteDetails(
  completedItem: CheckoutCompletedQuote
): CompletedQuoteDetails {
  const quoteDetails: QuoteDetails = basketItemToQuotes(completedItem.item)[0];

  return {
    ...completedItem.confirmation,
    ...quoteDetails,
    purchasedPaymentOption: quoteDetails.paymentOptions[0],
  };
}
